import type { ScenarioBatchInput } from "../../../../utils/portfolio-risk-concentration/api/scenario";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";

/** Every matched contract key -> its destination book, built once from the waterfall's own
 *  results — the exact `assignments` shape /scenario/monitor and /scenario/snapshot share. */
export function buildAssignments(draft: ScenarioDraft, facilities: { code: string; contractType: string }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of draft.waterfall?.conditions ?? []) {
    const code = c.destination?.facilityCode;
    if (!code) continue; // a destination with no facility (Equity, or a code mapped to none) has no book to re-book into
    // The book as the data spells it: the facility's contractType ("CAP 1", "ABS SCENARIO", ...). Before the list loads, the code itself
    // is the spelling of every facility but the two historical books.
    const contractType = facilities.find((f) => f.code === code)?.contractType ?? (code === "CAP1" ? "CAP 1" : code === "CAP2" ? "CAP 2" : code);
    for (const key of c.matchedKeys) out[key] = contractType;
  }
  return out;
}

/** contractScheduleKey -> destination investor code for every contract the waterfall gave a destination (the investor-code cards' moves). */
export function buildCodeAssignments(draft: ScenarioDraft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of draft.waterfall?.conditions ?? []) {
    if (!c.destination) continue;
    for (const key of c.matchedKeys) out[key] = c.destination.investorCode;
  }
  return out;
}

/** The scenario as /scenario/monitor and /scenario/snapshot read it: the effective date, the source pool (a code and window, or the uploaded
 *  contracts) and where the waterfall sent each contract. The caller adds what is its own (Loans / Leases, Measured in). */
export function scenarioBatchInput(draft: ScenarioDraft, facilities: { code: string; contractType: string }[]): ScenarioBatchInput {
  return {
    fileDate: draft.asOfDate,
    sourceInvestorCode: draft.sourceInvestorCode,
    dateFrom: draft.dateFrom,
    dateTo: draft.dateTo,
    assignments: buildAssignments(draft, facilities),
    codeAssignments: buildCodeAssignments(draft),
    sourceKeys: draft.sourceKeys ?? undefined,
  };
}
